<template>
  <!--
    ProjectList — the Project_List view (task 12.1).

    Renders the managed (non-proposal) projects in a Vuetify v-data-table with
    a live/hidden/proposal state chip and a Display_Order column, plus the
    search / category / live-hidden filter controls, clickable sortable column
    headers, and a clear-criteria control (Requirements 2.1–2.5, 3.1–3.6).

    All view-model derivation (row mapping with placeholders, state derivation,
    filtering, sorting, and 25-row pagination) is delegated to the pure,
    property-tested helpers in services/projectListDerivation.js so the table is
    a thin presentation layer over tested logic.

    States handled here (Requirements 2.6–2.9):
      - loading      : the table shows a loading indicator and row controls are
                       disabled until loading completes.
      - error/timeout: a load error, or a request that does not respond within
                       30 seconds, shows an error message with zero rows and a
                       retry control.
      - empty        : zero managed projects returns an empty state offering an
                       "add first project" control.
      - empty-result : active criteria that match no projects show an
                       empty-result message with a clear-criteria control.
      - pagination   : more than 25 projects are shown in pages of at most 25.

    Reorder (up/down), inline visibility toggle, and delete controls are added
    by task 12.2. Each acts optimistically and rolls back the displayed state if
    its Admin_Project_Api call fails; the container owns the success/error
    snackbars, so this component surfaces outcomes through emitted events
    (`reordered`, `visibility-changed`, `deleted`, and `error`). The delete
    action opens a confirmation dialog that names the project before proceeding
    (Requirements 8.4, 8.8, 8.9, 9.4, 9.5, 10.1, 10.3, 10.4, 10.5).
  -->
  <div class="project-list">
    <!-- Search / filter / clear-criteria controls (Requirement 3). -->
    <v-row class="project-list__controls" dense align="center">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="search"
          label="Search title or client"
          color="#e7511e"
          prepend-inner-icon="mdi-magnify"
          density="comfortable"
          variant="outlined"
          hide-details
          clearable
          data-test="search"
        />
      </v-col>

      <v-col cols="6" md="3">
        <v-select
          v-model="categoryFilter"
          :items="categoryOptions"
          item-title="title"
          item-value="value"
          label="Service category"
          color="#e7511e"
          density="comfortable"
          variant="outlined"
          hide-details
          data-test="category-filter"
        />
      </v-col>

      <v-col cols="6" md="3">
        <v-select
          v-model="stateFilter"
          :items="stateOptions"
          item-title="title"
          item-value="value"
          label="Website state"
          color="#e7511e"
          density="comfortable"
          variant="outlined"
          hide-details
          data-test="state-filter"
        />
      </v-col>

      <v-col cols="12" md="2" class="d-flex justify-end">
        <v-btn
          variant="text"
          color="#e7511e"
          :disabled="!hasActiveCriteria"
          data-test="clear-criteria"
          @click="clearCriteria"
        >
          Clear filters
        </v-btn>
      </v-col>
    </v-row>

    <!-- Error / timeout state: message, zero rows, retry (Requirement 2.7). -->
    <template v-if="loadError">
      <v-alert
        type="error"
        variant="tonal"
        class="my-4"
        data-test="load-error"
      >
        {{ loadError }}
      </v-alert>
      <div class="d-flex justify-center my-4">
        <v-btn color="#e7511e" data-test="retry" @click="retry">
          Retry
        </v-btn>
      </div>
    </template>

    <!-- Empty state: no managed projects at all (Requirement 2.8). -->
    <template v-else-if="!loading && rawProjects.length === 0">
      <div class="project-list__empty text-center my-8" data-test="empty-state">
        <div class="text-h6 mb-2">No projects yet</div>
        <div class="text-body-2 mb-4">
          Add your first project to manage what appears on the website.
        </div>
        <v-btn color="#e7511e" data-test="add-first" @click="addProject">
          Add first project
        </v-btn>
      </div>
    </template>

    <!-- The data table (Requirements 2.1–2.5, 2.6, 2.9, 3.4). -->
    <template v-else>
      <v-data-table
        :headers="headers"
        :items="pageRows"
        :items-per-page="PAGE_SIZE"
        :sort-by="sortBy"
        :loading="loading"
        item-value="id"
        class="project-list__table elevation-1"
        hide-default-footer
        data-test="project-table"
        @update:sort-by="onSortChange"
      >
        <!-- Live/hidden/proposal state chip (Requirements 2.3, 2.4, 2.5). -->
        <template #[`item.state`]="{ item }">
          <v-chip
            :color="stateColor(item.state)"
            size="small"
            variant="flat"
            data-test="state-chip"
          >
            {{ stateLabel(item.state) }}
          </v-chip>
        </template>

        <!-- Inline visibility toggle with optimistic update + rollback
             (Requirements 9.4, 9.5). -->
        <template #[`item.visibility`]="{ item }">
          <v-switch
            :model-value="item.showOnWebsite"
            :disabled="loading || pendingId === item.id"
            :loading="pendingId === item.id && pendingAction === 'visibility'"
            color="success"
            density="compact"
            hide-details
            inset
            data-test="visibility-toggle"
            @update:model-value="toggleVisibility(item)"
          />
        </template>

        <!-- Actions cell: reorder (up/down), edit, and delete. Row controls are
             disabled while loading (Requirement 2.6) and reorder is only
             available in the default Display_Order ascending view so the
             on-screen position matches the persisted order (Requirement 8.4). -->
        <template #[`item.actions`]="{ item }">
          <div class="d-flex ga-1 justify-end align-center">
            <v-btn
              icon="mdi-arrow-up"
              size="small"
              variant="text"
              color="#41281b"
              :disabled="loading || !canReorder || isFirst(item.id) || pendingId != null"
              data-test="move-up"
              @click="moveProject(item.id, 'up')"
            />
            <v-btn
              icon="mdi-arrow-down"
              size="small"
              variant="text"
              color="#41281b"
              :disabled="loading || !canReorder || isLast(item.id) || pendingId != null"
              data-test="move-down"
              @click="moveProject(item.id, 'down')"
            />
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              color="#e7511e"
              :disabled="loading"
              data-test="edit"
              @click="editProject(item)"
            />
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              :disabled="loading || pendingId != null"
              data-test="delete"
              @click="requestDelete(item)"
            />
          </div>
        </template>

        <!-- Empty-result message with a clear-criteria control (Requirement
             3.5). Only shown once loading has finished. -->
        <template #no-data>
          <div
            v-if="!loading"
            class="project-list__no-results text-center my-6"
            data-test="empty-result"
          >
            <div class="text-body-1 mb-3">
              No projects match the current search and filters.
            </div>
            <v-btn
              variant="text"
              color="#e7511e"
              :disabled="!hasActiveCriteria"
              data-test="clear-criteria-inline"
              @click="clearCriteria"
            >
              Clear filters
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination at 25 rows per page (Requirement 2.9). -->
      <div
        v-if="pageCount > 1"
        class="d-flex justify-center mt-4"
      >
        <v-pagination
          v-model="page"
          :length="pageCount"
          color="#e7511e"
          density="comfortable"
          data-test="pagination"
        />
      </div>
    </template>

    <!-- Delete confirmation dialog. Identifies the project by title and
         requires explicit confirmation before deleting (Requirements 10.1,
         10.3, 10.5). -->
    <v-dialog
      v-model="deleteDialog"
      max-width="480"
      data-test="delete-dialog"
    >
      <v-card>
        <v-card-title class="text-h6">Delete project</v-card-title>
        <v-card-text>
          Are you sure you want to delete
          <strong data-test="delete-dialog-title">{{ deleteTargetTitle }}</strong>?
          This removes it from the website and cannot be undone.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            variant="text"
            :disabled="pendingAction === 'delete'"
            data-test="delete-cancel"
            @click="cancelDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="pendingAction === 'delete'"
            data-test="delete-confirm"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// ProjectList — Project_List view (task 12.1).
import AdminProjectApi from 'services/AdminProjectApi'
import {
  PAGE_SIZE,
  ALL,
  STATE_LIVE,
  STATE_HIDDEN,
  STATE_PROPOSAL,
  deriveRows,
  filterRows,
  sortRows,
  paginate
} from 'services/projectListDerivation'

// The managed-project list request must fail if it does not respond within 30
// seconds (Requirement 2.7). AdminProjectApi.list() does not expose a timeout,
// so the fetch is raced against this deadline in loadProjects().
const LOAD_TIMEOUT_MS = 30000

// Default ordering matches the ascending Display_Order the server returns
// (Requirement 2.1).
const DEFAULT_SORT_COLUMN = 'displayOrder'
const DEFAULT_SORT_DIRECTION = 'asc'

// Best-effort extraction of a human-readable message from an API error while
// never leaking internal detail (the server sends generic messages).
function extractErrorMessage (err, fallback) {
  const data = err && err.response && err.response.data
  if (data) {
    if (typeof data.message === 'string' && data.message) return data.message
    if (typeof data.error === 'string' && data.error) return data.error
  }
  return fallback
}

export default {
  name: 'ProjectList',

  emits: [
    'add',
    'edit',
    'loaded',
    'reordered',
    'visibility-changed',
    'deleted',
    'error'
  ],

  data () {
    return {
      PAGE_SIZE,

      // Raw managed-project payloads from the Admin_Project_Api.
      rawProjects: [],

      // Load lifecycle (Requirements 2.6, 2.7).
      loading: false,
      loadError: '',

      // Active search / filter criteria (Requirement 3).
      search: '',
      categoryFilter: ALL,
      stateFilter: ALL,

      // Sort state, driven through the pure sortRows helper (Requirement 3.4).
      sortColumn: DEFAULT_SORT_COLUMN,
      sortDirection: DEFAULT_SORT_DIRECTION,

      // Current 1-based page (Requirement 2.9).
      page: 1,

      // Optimistic-action tracking. `pendingId` is the id of the project a
      // reorder / visibility / delete request is in flight for, and
      // `pendingAction` names that action so the correct control shows a
      // spinner and sibling controls disable (Requirements 8.9, 9.5, 10.5).
      pendingId: null,
      pendingAction: null,

      // Delete confirmation dialog state (Requirements 10.1, 10.3).
      deleteDialog: false,
      deleteTarget: null,

      // Fixed state-filter options.
      stateOptions: [
        { title: 'All states', value: ALL },
        { title: 'Live', value: STATE_LIVE },
        { title: 'Hidden', value: STATE_HIDDEN },
        { title: 'Proposal', value: STATE_PROPOSAL }
      ]
    }
  },

  computed: {
    // Column definitions for the v-data-table. Keys match the row view-model
    // property names so the controlled sort model maps directly onto the pure
    // sortRows helper.
    headers () {
      return [
        { title: 'Title', key: 'title', sortable: true },
        { title: 'Service category', key: 'serviceCategory', sortable: true },
        { title: 'Client', key: 'clientName', sortable: true },
        { title: 'State', key: 'state', sortable: true },
        { title: 'Order', key: 'displayOrder', sortable: true, align: 'end' },
        { title: 'Visible', key: 'visibility', sortable: false, align: 'center' },
        { title: 'Actions', key: 'actions', sortable: false, align: 'end' }
      ]
    },

    // The controlled sort model consumed by v-data-table.
    sortBy () {
      return [{ key: this.sortColumn, order: this.sortDirection }]
    },

    // All rows derived from the raw payloads, with placeholders filled in
    // (Requirement 2.2). Pure: deriveRows never throws or mutates.
    allRows () {
      return deriveRows(this.rawProjects)
    },

    // The category filter options are the distinct service categories present
    // in the derived rows, prefixed with an "All" sentinel (Requirement 3.2).
    categoryOptions () {
      const seen = new Set()
      const options = [{ title: 'All categories', value: ALL }]
      this.allRows.forEach(row => {
        const categories = Array.isArray(row.serviceCategories) && row.serviceCategories.length > 0
          ? row.serviceCategories
          : [row.serviceCategory]
        categories.forEach(category => {
          if (category && !seen.has(category)) {
            seen.add(category)
            options.push({ title: category, value: category })
          }
        })
      })
      return options
    },

    // Rows after applying the active search / category / state criteria
    // (Requirements 3.1, 3.2, 3.3, 3.6).
    filteredRows () {
      return filterRows(this.allRows, {
        search: this.search || '',
        category: this.categoryFilter,
        state: this.stateFilter
      })
    },

    // Filtered rows ordered by the selected column and direction
    // (Requirement 3.4).
    sortedRows () {
      return sortRows(this.filteredRows, this.sortColumn, this.sortDirection)
    },

    // The ordered rows chunked into pages of at most 25 (Requirement 2.9).
    pages () {
      return paginate(this.sortedRows, PAGE_SIZE)
    },

    pageCount () {
      return this.pages.length
    },

    // The rows for the current page (already derived, filtered, sorted, and
    // paginated by the pure helpers).
    pageRows () {
      if (this.pages.length === 0) return []
      const index = Math.min(this.page, this.pages.length) - 1
      return this.pages[index] || []
    },

    // Whether any search or filter criterion is currently active
    // (Requirements 3.5, 3.6).
    hasActiveCriteria () {
      return (
        (this.search || '').trim() !== '' ||
        this.categoryFilter !== ALL ||
        this.stateFilter !== ALL
      )
    },

    // The full managed set ordered by ascending Display_Order (id ascending as
    // the tie-break). Reorder operates on this global ordering — not on the
    // filtered / paginated view — so a moved project keeps a deterministic,
    // globally-unique Display_Order (Requirements 8.4, 8.5).
    orderedManaged () {
      return this.rawProjects.slice().sort((a, b) => {
        const ao = Number.isFinite(a && a.display_order) ? a.display_order : Infinity
        const bo = Number.isFinite(b && b.display_order) ? b.display_order : Infinity
        if (ao !== bo) return ao - bo
        const ai = (a && a.id != null) ? a.id : 0
        const bi = (b && b.id != null) ? b.id : 0
        return ai - bi
      })
    },

    // Reorder is only offered in the default Display_Order ascending view with
    // no active criteria, so the on-screen row position matches the persisted
    // order the up/down controls act on (Requirement 8.4).
    canReorder () {
      return (
        this.sortColumn === DEFAULT_SORT_COLUMN &&
        this.sortDirection === DEFAULT_SORT_DIRECTION &&
        !this.hasActiveCriteria
      )
    },

    // The title shown in the delete confirmation prompt (Requirement 10.1).
    deleteTargetTitle () {
      return (this.deleteTarget && this.deleteTarget.title) || 'this project'
    }
  },

  watch: {
    // Re-derived criteria can shrink the result set, so return to the first
    // page whenever the active criteria or ordering change.
    search () {
      this.page = 1
    },
    categoryFilter () {
      this.page = 1
    },
    stateFilter () {
      this.page = 1
    },
    sortColumn () {
      this.page = 1
    },
    sortDirection () {
      this.page = 1
    },

    // Keep the page within range if the result set shrinks for any reason.
    pageCount (count) {
      if (this.page > count) {
        this.page = count > 0 ? count : 1
      }
    }
  },

  created () {
    this.loadProjects()
  },

  methods: {
    // Fetch the managed projects, failing if the request errors or does not
    // respond within 30 seconds (Requirements 2.1, 2.7).
    async loadProjects () {
      this.loading = true
      this.loadError = ''

      let timer
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          reject(new Error('timeout'))
        }, LOAD_TIMEOUT_MS)
      })

      try {
        const res = await Promise.race([AdminProjectApi.list(), timeout])
        const projects = (res && res.data && res.data.projects) || []
        this.rawProjects = Array.isArray(projects) ? projects : []
        this.page = 1
        this.$emit('loaded', this.rawProjects)
      } catch (err) {
        // On error or timeout show the message and render zero rows.
        this.rawProjects = []
        this.loadError =
          err && err.message === 'timeout'
            ? 'The project list took too long to load. Please try again.'
            : extractErrorMessage(err, 'Unable to load projects. Please try again.')
      } finally {
        clearTimeout(timer)
        this.loading = false
      }
    },

    // Retry after a load failure (Requirement 2.7).
    retry () {
      this.loadProjects()
    },

    // Map the v-data-table controlled sort event onto our sort state so the
    // pure sortRows helper drives the ordering (Requirement 3.4).
    onSortChange (value) {
      if (Array.isArray(value) && value.length > 0 && value[0].key) {
        this.sortColumn = value[0].key
        this.sortDirection = value[0].order === 'desc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = DEFAULT_SORT_COLUMN
        this.sortDirection = DEFAULT_SORT_DIRECTION
      }
    },

    // Reset all active search / filter criteria (Requirements 3.5, 3.6).
    clearCriteria () {
      this.search = ''
      this.categoryFilter = ALL
      this.stateFilter = ALL
      this.page = 1
    },

    stateLabel (state) {
      if (state === STATE_LIVE) return 'Live'
      if (state === STATE_PROPOSAL) return 'Proposal'
      return 'Hidden'
    },

    stateColor (state) {
      if (state === STATE_LIVE) return 'success'
      if (state === STATE_PROPOSAL) return 'warning'
      return 'grey'
    },

    // Open the editor in create mode (Requirement 2.8). The container / route
    // handles the actual navigation.
    addProject () {
      this.$emit('add')
    },

    // Open the editor for an existing project. Reorder / visibility / delete
    // row controls are added by task 12.2.
    editProject (item) {
      if (item && item.id != null) {
        this.$emit('edit', item.id)
      }
    },

    // Position of a project within the global Display_Order ordering, used to
    // disable the up control on the first row and the down control on the last.
    isFirst (id) {
      const ordered = this.orderedManaged
      return ordered.length === 0 || (ordered[0] && ordered[0].id === id)
    },

    isLast (id) {
      const ordered = this.orderedManaged
      return (
        ordered.length === 0 ||
        (ordered[ordered.length - 1] && ordered[ordered.length - 1].id === id)
      )
    },

    // Move a project one position up or down within the managed set and persist
    // the new arrangement. The UI updates optimistically and rolls back to the
    // last saved order if the reorder request fails (Requirements 8.4, 8.8,
    // 8.9). The success/error confirmations are surfaced by the container via
    // emitted events.
    async moveProject (id, direction) {
      if (this.pendingId != null || !this.canReorder) return

      const ordered = this.orderedManaged
      const index = ordered.findIndex(p => p && p.id === id)
      if (index < 0) return

      const target = direction === 'up' ? index - 1 : index + 1
      if (target < 0 || target >= ordered.length) return

      // Snapshot the raw payloads for rollback (Requirement 8.9).
      const snapshot = this.rawProjects.map(p => ({ ...p }))

      // Compute the requested arrangement of ids.
      const reordered = ordered.slice()
      const [moved] = reordered.splice(index, 1)
      reordered.splice(target, 0, moved)
      const desiredIdOrder = reordered.map(p => p.id)

      // Permute the Display_Order values the managed set already holds onto the
      // new arrangement, preserving global uniqueness (mirrors the server's
      // applyReorder). Missing/invalid orders fall back to a 1..N assignment.
      const orderValues = ordered
        .map((p, i) => (Number.isFinite(p && p.display_order) ? p.display_order : i + 1))
        .sort((a, b) => a - b)
      const newOrderById = new Map()
      reordered.forEach((p, i) => {
        newOrderById.set(p.id, orderValues[i])
      })

      // Apply optimistically.
      this.rawProjects = this.rawProjects.map(p =>
        newOrderById.has(p.id)
          ? { ...p, display_order: newOrderById.get(p.id) }
          : p
      )

      this.pendingId = id
      this.pendingAction = 'reorder'

      try {
        await AdminProjectApi.reorder(desiredIdOrder)
        this.$emit('reordered', {
          desiredIdOrder,
          message: 'Project order updated.'
        })
      } catch (err) {
        // Restore the last successfully saved order (Requirement 8.9).
        this.rawProjects = snapshot
        this.$emit('error', {
          message: extractErrorMessage(
            err,
            'Unable to save the new order. The previous order has been restored.'
          )
        })
      } finally {
        this.pendingId = null
        this.pendingAction = null
      }
    },

    // Toggle a project's live/hidden state optimistically, rolling back the
    // displayed state if the request fails (Requirements 9.4, 9.5).
    async toggleVisibility (item) {
      if (!item || item.id == null || this.pendingId != null) return

      const id = item.id
      const nextValue = !item.showOnWebsite
      const snapshot = this.rawProjects.map(p => ({ ...p }))

      // Apply optimistically to the raw payload the row model derives from.
      this.rawProjects = this.rawProjects.map(p =>
        p.id === id ? { ...p, show_on_website: nextValue } : p
      )

      this.pendingId = id
      this.pendingAction = 'visibility'

      try {
        await AdminProjectApi.setVisibility(id, nextValue)
        this.$emit('visibility-changed', {
          id,
          showOnWebsite: nextValue,
          message: nextValue
            ? 'Project is now live on the website.'
            : 'Project is now hidden from the website.'
        })
      } catch (err) {
        // Restore the previous displayed state (Requirement 9.5).
        this.rawProjects = snapshot
        this.$emit('error', {
          message: extractErrorMessage(
            err,
            'Unable to change the project visibility. Please try again.'
          )
        })
      } finally {
        this.pendingId = null
        this.pendingAction = null
      }
    },

    // Open the delete confirmation prompt for a project (Requirement 10.1).
    requestDelete (item) {
      if (!item || item.id == null) return
      this.deleteTarget = { id: item.id, title: item.title }
      this.deleteDialog = true
    },

    // Cancel the confirmation prompt without acting on the project
    // (Requirement 10.3).
    cancelDelete () {
      this.deleteDialog = false
      this.deleteTarget = null
    },

    // Confirm and perform the delete. On success the project is removed from
    // the list; on error it is retained and an error surfaced (Requirements
    // 10.4, 10.5).
    async confirmDelete () {
      if (!this.deleteTarget || this.deleteTarget.id == null) return

      const { id, title } = this.deleteTarget
      this.pendingId = id
      this.pendingAction = 'delete'

      try {
        await AdminProjectApi.remove(id)
        this.rawProjects = this.rawProjects.filter(p => p.id !== id)
        this.deleteDialog = false
        this.deleteTarget = null
        this.$emit('deleted', {
          id,
          title,
          message: 'Project deleted.'
        })
      } catch (err) {
        // Retain the project in the list (Requirement 10.5).
        this.$emit('error', {
          message: extractErrorMessage(
            err,
            'Unable to delete the project. Please try again.'
          )
        })
      } finally {
        this.pendingId = null
        this.pendingAction = null
      }
    }
  }
}
</script>

<style scoped>
.project-list__controls {
  margin-bottom: 8px;
}

.project-list__empty,
.project-list__no-results {
  color: #41281b;
}
</style>
